import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { MessageRequest } from '@skyeng/libs/skypro/messages';
import { SkyproCommunicationConfig } from '../const';
import {
  Communication,
  CommunicationApiResponse,
  CommunicationApiResponseSuccess,
  MessageRequestWithMeta,
  StudentCommunication,
  StudentCommunicationApiResponse,
  StudentLastCommunicationDateApiResponse,
} from '../interface';
import { mapApiResponseToCommunication } from '../helpers';

@Injectable({ providedIn: 'root' })
export class CommunicationApiService {
  private http = inject(HttpClient);
  private skyproCommunicationConfig = inject(SkyproCommunicationConfig);
  private endpoint = `${this.skyproCommunicationConfig.communicationApiDomain}/api/v1/communication`;

  getCommunication(
    communicationId: number,
    lastThreadId: number | null,
    sortByOwnThreads = true,
  ): Observable<Communication> {
    let params: { lastThreadId?: string; sortByOwnThreads: boolean } = { sortByOwnThreads: sortByOwnThreads };

    if (lastThreadId) {
      params = { ...params, lastThreadId: String(lastThreadId) };
    }

    return this.http
      .get<CommunicationApiResponse>(`${this.endpoint}/${communicationId}`, {
        withCredentials: true,
        params,
      })
      .pipe(map(mapApiResponseToCommunication));
  }

  getStudentCommunications(): Observable<StudentCommunication> {
    return this.http
      .get<StudentCommunicationApiResponse>(
        `${this.skyproCommunicationConfig.communicationApiDomain}/student-cabinet/api/gateway/v1/student-communications`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        map(({ extendedCommunications }) => ({
          extendedCommunications: extendedCommunications.map(extendedCommunication => ({
            ...extendedCommunication,
            communication: mapApiResponseToCommunication(extendedCommunication.communication),
          })),
        })),
      );
  }

  getStudentLastCommunicationDate(): Observable<string | null> {
    return this.http
      .get<StudentLastCommunicationDateApiResponse>(
        `${this.skyproCommunicationConfig.communicationApiDomain}/api/v1/communication-last-message-date`,
        {
          withCredentials: true,
        },
      )
      .pipe(map(({ lastCommunicationDate }) => lastCommunicationDate));
  }

  createThread(message: MessageRequest, communicationId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread`,
      { communicationId, text: message.text, attachments: message.attachments },
      {
        withCredentials: true,
      },
    );
  }

  editThread(message: MessageRequest, threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.put<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread/${threadId}`,
      { text: message.text, attachments: message.attachments },
      {
        withCredentials: true,
      },
    );
  }

  deleteThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.delete<CommunicationApiResponseSuccess>(`${this.endpoint}/thread/${threadId}`, {
      withCredentials: true,
    });
  }

  pinThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread/${threadId}/pin`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  unpinThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread/${threadId}/unpin`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  hideThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread/${threadId}/hide`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  showThread(threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/thread/${threadId}/show`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  replyInThread(message: MessageRequestWithMeta, threadId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/message`,
      { threadId, text: message.text, attachments: message.attachments, meta: message.meta },
      {
        withCredentials: true,
      },
    );
  }

  editMessageInThread(message: MessageRequest, messageId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.put<CommunicationApiResponseSuccess>(
      `${this.endpoint}/message/${messageId}`,
      { text: message.text, attachments: message.attachments },
      {
        withCredentials: true,
      },
    );
  }

  deleteMessageInThread(messageId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.delete<CommunicationApiResponseSuccess>(`${this.endpoint}/message/${messageId}`, {
      withCredentials: true,
    });
  }

  createLike(contentId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(`${this.endpoint}/like/${contentId}`, null, {
      withCredentials: true,
    });
  }

  deleteLike(contentId: number): Observable<CommunicationApiResponseSuccess> {
    return this.http.delete<CommunicationApiResponseSuccess>(`${this.endpoint}/like/${contentId}`, {
      withCredentials: true,
    });
  }

  sendReport(contentId: number, comment: string): Observable<CommunicationApiResponseSuccess> {
    return this.http.post<CommunicationApiResponseSuccess>(
      `${this.endpoint}/report/${contentId}`,
      { comment },
      {
        withCredentials: true,
      },
    );
  }
}
